import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { FaArrowLeft, FaCheck, FaImage, FaPlus, FaSpinner, FaTrash } from 'react-icons/fa';
import { API_BASE, parseJsonResponse, getUserErrorMessage } from '../../utils/apiBase';
import { authFetch } from '../../utils/apiAuth';
import { getAffiliatePageStyles } from '../affiliates/affiliateStyles';
import DealDatePicker from '../../components/DealDatePicker';
import RichTextEditor from '../../components/RichTextEditor';
import NumericInput from '../../components/NumericInput';
import { normalizeDescriptionHtml } from '../../utils/richText';
import '../affiliates/affiliates.css';
import './deals.css';

function formatRs(n) {
  return `Rs ${Number(n || 0).toLocaleString('en-PK')}`;
}

function sizeSalePrice(size) {
  const price = Number(size?.price) || 0;
  const discount = Number(size?.discount) || 0;
  if (discount > 0) return Math.round(price * (1 - discount / 100));
  return price;
}

export default function DealForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const imageAddInputRef = useRef(null);
  const imageReplaceInputRef = useRef(null);
  const [replaceImageIndex, setReplaceImageIndex] = useState(null);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [form, setForm] = useState({
    name: '',
    description: '',
    images: [],
    dealPrice: '',
    status: 'active',
    startDate: '',
    endDate: '',
    items: [],
  });

  const styles = getAffiliatePageStyles(isMobile, isSmallMobile);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/v1/product?limit=200&status=active`);
      const result = await parseJsonResponse(response);
      setProducts(result.data || []);
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to load products.'));
    }
  }, []);

  const fetchDeal = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/v1/deals/${id}`);
      const result = await parseJsonResponse(response);
      const deal = result.data;
      setForm({
        name: deal.name || '',
        description: deal.description || '',
        images:
          Array.isArray(deal.images) && deal.images.length
            ? deal.images
            : deal.image
              ? [deal.image]
              : [],
        dealPrice: String(deal.dealPrice ?? ''),
        status: deal.status || 'active',
        startDate: deal.startDate ? deal.startDate.slice(0, 10) : '',
        endDate: deal.endDate ? deal.endDate.slice(0, 10) : '',
        items: (deal.items || []).map((item) => ({
          productId: item.productId?._id || item.productId,
          sizeId: item.sizeId,
          quantity: item.quantity || 1,
          productName: item.product?.name,
          sizeMl: item.size?.ml,
        })),
      });
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to load deal.'));
      navigate('/deals/list');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchProducts();
    fetchDeal();
  }, [fetchProducts, fetchDeal]);

  const originalTotal = useMemo(() => {
    return form.items.reduce((sum, item) => {
      const product = products.find((p) => p._id === item.productId);
      const size = product?.sizes?.find((s) => s._id === item.sizeId);
      if (!size) return sum;
      return sum + sizeSalePrice(size) * (item.quantity || 1);
    }, 0);
  }, [form.items, products]);

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => p.name?.toLowerCase().includes(q));
  }, [products, productSearch]);

  const selectedProductIds = useMemo(
    () => new Set(form.items.map((item) => item.productId)),
    [form.items]
  );

  const sortedFilteredProducts = useMemo(() => {
    return [...filteredProducts].sort((a, b) => {
      const aSelected = selectedProductIds.has(a._id);
      const bSelected = selectedProductIds.has(b._id);
      if (aSelected === bSelected) return 0;
      return aSelected ? -1 : 1;
    });
  }, [filteredProducts, selectedProductIds]);

  const uploadDealImageFile = async (file) => {
    if (!file) return null;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Upload JPG, PNG, or WEBP only');
      return null;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image must be 8MB or smaller');
      return null;
    }

    const formData = new FormData();
    formData.append('image', file);

    const response = await authFetch(`${API_BASE}/v1/deals/upload-image`, {
      method: 'POST',
      body: formData,
    });
    const result = await parseJsonResponse(response);
    const imageUrl = result.data?.imageUrl;
    if (!imageUrl) throw new Error('Upload did not return an image URL');
    return imageUrl;
  };

  const handleAddImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const toastId = toast.loading('Uploading deal image…');
    setImageUploading(true);

    try {
      const imageUrl = await uploadDealImageFile(file);
      if (!imageUrl) return;
      setForm((prev) => ({
        ...prev,
        images: [...prev.images, imageUrl],
      }));
      toast.success('Deal image uploaded', { id: toastId });
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Image upload failed.'), { id: toastId });
    } finally {
      setImageUploading(false);
      if (imageAddInputRef.current) imageAddInputRef.current.value = '';
    }
  };

  const handleReplaceImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file || replaceImageIndex == null) return;

    const toastId = toast.loading('Replacing image…');
    setImageUploading(true);
    try {
      const imageUrl = await uploadDealImageFile(file);
      if (!imageUrl) return;
      setForm((prev) => ({
        ...prev,
        images: prev.images.map((url, idx) => (idx === replaceImageIndex ? imageUrl : url)),
      }));
      toast.success('Deal image replaced', { id: toastId });
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Image upload failed.'), { id: toastId });
    } finally {
      setImageUploading(false);
      setReplaceImageIndex(null);
      if (imageReplaceInputRef.current) imageReplaceInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index) => {
    setForm((prev) => ({
      ...prev,
      images: prev.images.filter((_, idx) => idx !== index),
    }));
  };

  const triggerReplaceImage = (index) => {
    setReplaceImageIndex(index);
    imageReplaceInputRef.current?.click();
  };

  const addProduct = (product) => {
    const defaultSize = product.sizes?.[0];
    if (!defaultSize) {
      toast.error('This product has no sizes');
      return;
    }
    if (form.items.some((i) => i.productId === product._id)) {
      toast.error('Product already added to this deal');
      return;
    }
    setForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          productId: product._id,
          sizeId: defaultSize._id,
          quantity: 1,
          productName: product.name,
          sizeMl: defaultSize.ml,
        },
      ],
    }));
  };

  const removeProductById = (productId) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.productId !== productId),
    }));
  };

  const toggleProduct = (product) => {
    if (selectedProductIds.has(product._id)) {
      removeProductById(product._id);
      return;
    }
    addProduct(product);
  };

  const updateItem = (index, field, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      if (field === 'productId') {
        const product = products.find((p) => p._id === value);
        const size = product?.sizes?.[0];
        items[index].sizeId = size?._id || '';
        items[index].productName = product?.name;
        items[index].sizeMl = size?.ml;
      }
      if (field === 'sizeId') {
        const product = products.find((p) => p._id === items[index].productId);
        const size = product?.sizes?.find((s) => s._id === value);
        items[index].sizeMl = size?.ml;
      }
      return { ...prev, items };
    });
  };

  const removeItem = (index) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Deal name is required');
      return;
    }
    if (form.items.length === 0) {
      toast.error('Add at least one product');
      return;
    }
    const dealPrice = Number(form.dealPrice);
    if (!Number.isFinite(dealPrice) || dealPrice < 0) {
      toast.error('Enter a valid deal price');
      return;
    }
    if (dealPrice > originalTotal) {
      toast.error('Deal price cannot be higher than combined product price');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: normalizeDescriptionHtml(form.description),
        image: form.images[0] || '',
        images: form.images,
        dealPrice,
        status: form.status,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        items: form.items.map(({ productId, sizeId, quantity }) => ({
          productId,
          sizeId,
          quantity: Number(quantity) || 1,
        })),
      };

      const url = isEdit ? `${API_BASE}/v1/deals/${id}` : `${API_BASE}/v1/deals`;
      const response = await authFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      await parseJsonResponse(response);
      toast.success(isEdit ? 'Deal updated' : 'Deal created');
      navigate('/deals/list');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to save deal.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.mainWrapper}>
        <p className="deal-loading-text">Loading deal...</p>
      </div>
    );
  }

  return (
    <div style={styles.mainWrapper}>
      <Toaster position="top-center" />
      <div style={styles.container}>
        <button
          type="button"
          className="deal-back-link"
          onClick={() => navigate('/deals/list')}
        >
          <FaArrowLeft />
          Back to deals
        </button>

        <div style={styles.headerRow}>
          <div>
            <h1 style={styles.headerTitle}>{isEdit ? 'Edit Deal' : 'Create Deal'}</h1>
            <p style={styles.breadcrumbs}>
              Home • Deals • {isEdit ? 'Edit' : 'Create New'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={styles.formSection} autoComplete="off">
          <div style={styles.formGrid2}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Deal name *</label>
              <input
                className="affiliate-form-input"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Summer Duo Pack"
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Status</label>
              <select
                className="affiliate-form-input"
                value={form.status}
                onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Description</label>
            <RichTextEditor
              value={form.description}
              onChange={(html) => setForm((p) => ({ ...p, description: html }))}
              placeholder="Write an attractive deal description with headings, bold text, lists, and more..."
              disabled={saving || imageUploading}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Deal images (optional)</label>
            <input
              ref={imageAddInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={handleAddImage}
              style={{ display: 'none' }}
            />
            <input
              ref={imageReplaceInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={handleReplaceImage}
              style={{ display: 'none' }}
            />

            <div className="deal-image-upload">
              <button
                type="button"
                className={`deal-image-dropzone${imageUploading ? ' deal-image-dropzone--busy' : ''}`}
                onClick={() => !imageUploading && imageAddInputRef.current?.click()}
                disabled={imageUploading || saving}
              >
                {imageUploading ? (
                  <FaSpinner className="deal-image-dropzone-icon deal-image-dropzone-icon--spin" />
                ) : (
                  <FaImage className="deal-image-dropzone-icon" />
                )}
                <p className="deal-image-dropzone-title">
                  {imageUploading ? 'Uploading to Cloudinary…' : 'Click to add deal image'}
                </p>
                <p className="deal-image-dropzone-hint">JPG, PNG or WEBP — max 8MB</p>
              </button>

              {form.images.length ? (
                <div className="deal-image-grid">
                  {form.images.map((url, idx) => (
                    <div key={`${url}-${idx}`} className="deal-image-card">
                      <div className="deal-image-preview-wrap">
                        <img src={url} alt={`Deal preview ${idx + 1}`} className="deal-image-preview" />
                        {idx === 0 ? <span className="deal-image-primary-badge">Primary</span> : null}
                      </div>
                      <div className="deal-image-actions">
                        <button
                          type="button"
                          className="deal-image-change-btn"
                          onClick={() => triggerReplaceImage(idx)}
                          disabled={imageUploading || saving}
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          className="deal-image-remove-btn"
                          onClick={() => handleRemoveImage(idx)}
                          disabled={imageUploading || saving}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <div style={styles.formGrid2}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Special deal price (PKR) *</label>
              <NumericInput
                className="affiliate-form-input"
                min="0"
                value={form.dealPrice}
                onChange={(e) => setForm((p) => ({ ...p, dealPrice: e.target.value }))}
                required
              />
            </div>
            <div className="deal-summary-box">
              <p className="deal-summary-label">Regular total</p>
              <p className="deal-summary-value">{formatRs(originalTotal)}</p>
              {originalTotal > Number(form.dealPrice) && form.dealPrice !== '' && (
                <p className="deal-summary-save">
                  Customer saves {formatRs(originalTotal - Number(form.dealPrice))}
                </p>
              )}
            </div>
          </div>

          <div style={styles.formGrid2}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Start date (optional)</label>
              <DealDatePicker
                value={form.startDate}
                onChange={(startDate) =>
                  setForm((p) => ({
                    ...p,
                    startDate,
                    endDate:
                      p.endDate && startDate && p.endDate < startDate ? '' : p.endDate,
                  }))
                }
                placeholder="Pick start date"
                max={form.endDate || undefined}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>End date (optional)</label>
              <DealDatePicker
                value={form.endDate}
                onChange={(endDate) => setForm((p) => ({ ...p, endDate }))}
                placeholder="Pick end date"
                min={form.startDate || undefined}
              />
            </div>
          </div>

          <div className="deal-form-section">
            <h2 className="deal-form-section-title">Products in this deal</h2>

            {form.items.length === 0 ? (
              <p style={{ margin: 0, color: '#9ca3af', fontSize: '13px' }}>
                No products selected yet.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: '10px' }}>
                {form.items.map((item, index) => {
                  const product = products.find((p) => p._id === item.productId);
                  const thumb = product?.images?.[0];
                  return (
                    <div
                      key={`${item.productId}-${index}`}
                      className="deal-selected-row deal-selected-row--highlight"
                    >
                      {thumb ? (
                        <img src={thumb} alt="" className="deal-selected-thumb" />
                      ) : null}
                      <div className="deal-selected-info">
                        <span className="deal-selected-name">
                          {item.productName || product?.name || 'Product'}
                        </span>
                        <select
                          className="affiliate-form-input"
                          value={item.sizeId}
                          onChange={(e) => updateItem(index, 'sizeId', e.target.value)}
                        >
                          {(product?.sizes || []).map((size) => (
                            <option key={size._id} value={size._id}>
                              {size.ml}ml — {formatRs(sizeSalePrice(size))}
                            </option>
                          ))}
                        </select>
                      </div>
                      <NumericInput
                        min="1"
                        className="deal-qty-input"
                        value={item.quantity === 0 || item.quantity ? item.quantity : ''}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                      />
                      <button
                        type="button"
                        className="deal-remove-btn"
                        onClick={() => removeItem(index)}
                        aria-label="Remove product"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="deal-form-section">
            <h2 className="deal-form-section-title">Add products</h2>
            <input
              className="affiliate-form-input"
              placeholder="Search products..."
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
            />
            <div className="deal-product-grid">
              {sortedFilteredProducts.slice(0, 48).map((product) => {
                const isSelected = selectedProductIds.has(product._id);
                const thumb = product.images?.[0];

                return (
                  <button
                    key={product._id}
                    type="button"
                    className={`deal-product-pick${isSelected ? ' deal-product-pick--selected' : ''}`}
                    onClick={() => toggleProduct(product)}
                    title={isSelected ? 'Click to remove from deal' : 'Click to add to deal'}
                  >
                    <div className="deal-product-pick-body">
                      {thumb ? (
                        <img src={thumb} alt="" className="deal-product-pick-thumb" />
                      ) : null}
                      <div>
                        <span className="deal-product-pick-name">{product.name}</span>
                        {isSelected && <div className="deal-product-pick-badge">In deal</div>}
                      </div>
                    </div>
                    {isSelected ? (
                      <FaCheck className="deal-product-pick-icon" size={14} />
                    ) : (
                      <FaPlus className="deal-product-pick-icon" size={12} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="submit"
            style={{ ...styles.primaryButton, alignSelf: 'flex-start', marginTop: '4px' }}
            disabled={saving || form.dealPrice === ''}
          >
            {saving ? 'Saving...' : isEdit ? 'Update Deal' : 'Create Deal'}
          </button>
        </form>
      </div>
    </div>
  );
}
